import { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TreePine, Lock, Share2, Link2, Sparkles, Heart, Send } from 'lucide-react';
import treeBg from '@/assets/living-tree-v5-bg.png';
import { FireflyParticles } from '@/components/living-tree/FireflyParticles';
import { EmptyTreeState } from '@/components/living-tree/EmptyTreeState';
import {
  announcementToFruit,
  BRANCH_ANCHORS,
  type TreeFruit,
} from '@/components/living-tree/types';
import { supabase } from '@/integrations/supabase/client';
import { BrandLogo } from '@/components/BrandLogo';
import { Button } from '@/components/ui/button';
import type { Announcement } from '@/types/announcement';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { copyWithToast } from '@/lib/shareLaunch';
import { ALL_VEIN_PATHS, getPathWidth, getPathColor } from '@/components/living-tree/veinPaths';

// ─── Tree stage logic (identical to LivingTreeV5) ───────────────────────────
function getTreeStage(count: number) {
  let brightness = 0.4;
  if (count > 3) brightness = 0.5;
  if (count > 10) brightness = 0.6;
  if (count > 25) brightness = 0.75;
  if (count > 40) brightness = 0.85;
  if (count > 60) brightness = 0.95;
  if (count >= 100) brightness = 1;

  let trunkGlow = 0;
  if (count >= 10) trunkGlow = 0.15;
  if (count >= 35) trunkGlow = 0.35;
  if (count >= 50) trunkGlow = 0.5;
  if (count >= 75) trunkGlow = 0.75;
  if (count >= 100) trunkGlow = 1;

  let skyPhase = 0;
  if (count >= 25) skyPhase = 1;
  if (count >= 40) skyPhase = 2;
  if (count >= 60) skyPhase = 3;
  if (count >= 100) skyPhase = 4;

  let energyFlow = 0;
  if (count >= 25) energyFlow = 0.2;
  if (count >= 50) energyFlow = 0.5;
  if (count >= 75) energyFlow = 0.8;
  if (count >= 100) energyFlow = 1;

  return { brightness, trunkGlow, skyPhase, energyFlow };
}

function getSkyGradient(phase: number): string {
  switch (phase) {
    case 0: return 'radial-gradient(ellipse at 50% 90%, hsla(160, 30%, 6%, 0.9) 0%, hsla(200, 25%, 4%, 0.95) 100%)';
    case 1: return 'radial-gradient(ellipse at 50% 85%, hsla(220, 20%, 12%, 0.8) 0%, hsla(240, 15%, 8%, 0.9) 60%, hsla(30, 20%, 10%, 0.3) 100%)';
    case 2: return 'radial-gradient(ellipse at 50% 80%, hsla(25, 40%, 18%, 0.6) 0%, hsla(200, 15%, 12%, 0.7) 50%, hsla(35, 50%, 20%, 0.4) 100%)';
    case 3: return 'radial-gradient(ellipse at 50% 75%, hsla(30, 60%, 25%, 0.5) 0%, hsla(35, 45%, 22%, 0.5) 40%, hsla(40, 55%, 30%, 0.3) 100%)';
    case 4: return 'radial-gradient(ellipse at 50% 60%, hsla(40, 70%, 35%, 0.4) 0%, hsla(35, 50%, 30%, 0.35) 40%, hsla(45, 60%, 40%, 0.2) 100%)';
    default: return 'none';
  }
}

function assignFruitPositions(fruits: TreeFruit[]) {
  return fruits.map((fruit, i) => {
    const anchor = BRANCH_ANCHORS[i % BRANCH_ANCHORS.length];
    const jitterX = Math.sin(i * 7.3) * 2;
    const jitterY = Math.cos(i * 5.1) * 2;
    return { fruit, x: anchor.x + jitterX, y: anchor.y + jitterY };
  });
}

function PublicFruitNode({ x, y, index }: { x: number; y: number; index: number }) {
  return (
    <motion.div
      className="absolute z-[2]"
      style={{
        left: `${x}%`,
        top: `${y}%`,
        transform: 'translate(-50%, -50%)',
      }}
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.05 * index, type: 'spring', stiffness: 200, damping: 15 }}
    >
      <div
        className="w-4 h-4 md:w-5 md:h-5 rounded-full border border-primary/30"
        style={{
          background: 'radial-gradient(circle at 35% 35%, hsla(140, 50%, 65%, 0.9), hsla(140, 40%, 40%, 0.7))',
          boxShadow: '0 0 8px hsla(140, 50%, 50%, 0.3)',
        }}
      />
    </motion.div>
  );
}

function getGrowthLabel(count: number): string {
  if (count >= 100) return 'Radiant — fully alive';
  if (count >= 75) return 'Illuminated — energy flows';
  if (count >= 60) return 'Sunrise — sky brightens';
  if (count >= 40) return 'Dawn — warmth appears';
  if (count >= 25) return 'Rooted — golden roots grow';
  if (count >= 10) return 'Growing — leaves glow';
  if (count >= 5) return 'Awakening — birds arrive';
  if (count > 0) return 'Seedling — just beginning';
  return 'Dormant — no messages yet';
}

function getGrowthEmoji(count: number): string {
  if (count >= 100) return '🌞';
  if (count >= 75) return '🌅';
  if (count >= 60) return '🌄';
  if (count >= 40) return '🌇';
  if (count >= 25) return '🌿';
  if (count >= 10) return '🌱';
  if (count >= 5) return '🐦';
  if (count > 0) return '🌰';
  return '🌑';
}

const EMOTIONAL_HEADLINES = [
  "This tree grew from real moments.",
  "Every light you see is something that was finally said.",
  "This is what connection looks like.",
];

export default function PublicTreeView() {
  const { userId } = useParams<{ userId: string }>();
  const containerRef = useRef<HTMLDivElement>(null);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [userName, setUserName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  // Stable random headline per session
  const headline = useMemo(
    () => EMOTIONAL_HEADLINES[Math.floor(Math.random() * EMOTIONAL_HEADLINES.length)],
    []
  );

  useDocumentHead({
    title: userName ? `${userName}'s Living Tree — Say It Now` : 'Living Tree — Say It Now',
    description: 'Watch this Living Tree grow with every message shared. Each fruit represents a moment of connection.',
  });

  useEffect(() => {
    if (!userId) return;
    const load = async () => {
      setIsLoading(true);
      const [annRes, profileRes] = await Promise.all([
        supabase
          .from('announcements')
          .select('id, created_at, recipient_name, relationship, template, animation_theme, status, public_slug, published_at')
          .eq('user_id', userId)
          .eq('status', 'approved')
          .order('created_at', { ascending: false }),
        supabase
          .from('profiles')
          .select('full_name')
          .eq('user_id', userId)
          .maybeSingle(),
      ]);
      if (annRes.data) {
        setAnnouncements(annRes.data as unknown as Announcement[]);
      }
      if (profileRes.data?.full_name) {
        setUserName(profileRes.data.full_name.split(' ')[0]);
      }
      setIsLoading(false);
    };
    load();
  }, [userId]);

  const fruits = useMemo(() => announcements.map(announcementToFruit), [announcements]);
  const positioned = useMemo(() => assignFruitPositions(fruits), [fruits]);
  const stage = getTreeStage(fruits.length);
  const isEmpty = fruits.length === 0;

  const handleShareLink = () => {
    copyWithToast(window.location.href, 'Tree link copied!');
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: userName ? `${userName}'s Living Tree` : 'Living Tree',
          text: 'See this Living Tree — every light is something that was finally said.',
          url: window.location.href,
        });
      } catch {
        handleShareLink();
      }
    } else {
      handleShareLink();
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}>
          <TreePine className="h-8 w-8 text-primary" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Page-level ambient glow */}
      <div
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          background: 'radial-gradient(ellipse 80% 60% at 50% 30%, hsla(160, 40%, 12%, 0.4) 0%, transparent 70%)',
        }}
      />

      {/* Header */}
      <header className="relative z-10 border-b border-border/20 bg-background/60 backdrop-blur-xl">
        <div className="container flex items-center justify-between py-3 px-4">
          <Link to="/">
            <BrandLogo size="sm" />
          </Link>
          <Link to="/signup">
            <Button size="sm" className="rounded-xl gap-1.5 shadow-lg">
              <TreePine className="h-4 w-4" />
              Grow Your Own Tree
            </Button>
          </Link>
        </div>
      </header>

      <main className="relative z-10">
        {/* ─── Hero Section ─── */}
        <section className="pt-8 pb-4 px-4 text-center max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-medium mb-4">
              <Sparkles className="h-3 w-3" />
              Living Tree
            </div>

            <h1 className="text-2xl md:text-4xl font-bold text-foreground font-heading leading-tight mb-3">
              {userName ? (
                <>
                  <span className="text-primary">{userName}'s</span> Living Tree
                </>
              ) : (
                'A Living Tree'
              )}
            </h1>

            <p className="text-base md:text-lg text-muted-foreground/80 italic leading-relaxed max-w-md mx-auto">
              "{headline}"
            </p>
          </motion.div>
        </section>

        {/* ─── Progress Indicator ─── */}
        <motion.div
          className="text-center mb-4 px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-2xl bg-card/50 border border-border/30 backdrop-blur-sm">
            <span className="text-lg">{getGrowthEmoji(fruits.length)}</span>
            <div className="text-left">
              <p className="text-sm font-semibold text-foreground">
                {fruits.length} {fruits.length === 1 ? 'message' : 'messages'} shared
              </p>
              <p className="text-xs text-muted-foreground">{getGrowthLabel(fruits.length)}</p>
            </div>
          </div>
        </motion.div>

        {/* ─── Tree Container (IDENTICAL to dashboard) ─── */}
        <div className="px-4 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <div
              ref={containerRef}
              className="relative mx-auto rounded-2xl overflow-hidden"
              style={{ aspectRatio: '4 / 3', maxHeight: '700px' }}
            >
              {/* Sky */}
              <motion.div
                className="absolute inset-0 rounded-2xl overflow-hidden"
                style={{ background: getSkyGradient(stage.skyPhase) }}
                animate={{ opacity: stage.skyPhase > 0 ? 1 : 0 }}
                transition={{ duration: 3 }}
              />

              {/* Horizon glow */}
              {stage.skyPhase >= 2 && (
                <motion.div
                  className="absolute inset-0 pointer-events-none rounded-2xl overflow-hidden"
                  style={{
                    background: `radial-gradient(ellipse 80% 30% at 50% 85%, hsla(35, 70%, 50%, ${stage.skyPhase >= 4 ? 0.25 : 0.15}) 0%, transparent 70%)`,
                  }}
                  animate={{ opacity: [0.6, 1, 0.6] }}
                  transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                />
              )}

              {/* Feathering */}
              <div
                className="absolute inset-0 z-[1] pointer-events-none"
                style={{
                  background: `
                    linear-gradient(to bottom, hsl(var(--background)) 0%, transparent 12%, transparent 85%, hsl(var(--background)) 100%),
                    linear-gradient(to right, hsl(var(--background)) 0%, transparent 8%, transparent 92%, hsl(var(--background)) 100%)
                  `,
                }}
              />

              {/* Tree image */}
              <motion.div
                className="absolute inset-0"
                style={{
                  backgroundImage: `url(${treeBg})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center center',
                  backgroundRepeat: 'no-repeat',
                  opacity: stage.brightness,
                }}
                animate={{
                  scale: [1, 1.02, 1],
                  filter: [`brightness(${stage.brightness})`, `brightness(${stage.brightness * 1.1})`, `brightness(${stage.brightness})`],
                }}
                transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
              />

              {/* Trunk glow */}
              {stage.trunkGlow > 0 && (
                <motion.div
                  className="absolute pointer-events-none"
                  style={{
                    left: '46%', right: '46%', top: '25%', bottom: '15%',
                    background: `linear-gradient(to top, hsla(40, 80%, 50%, ${stage.trunkGlow * 0.4}), hsla(45, 70%, 60%, ${stage.trunkGlow * 0.2}) 50%, transparent 100%)`,
                    filter: `blur(${8 + stage.trunkGlow * 12}px)`,
                    borderRadius: '50%',
                  }}
                  animate={{ opacity: [0.5, 1, 0.5], scaleX: [0.9, 1.1, 0.9] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                />
              )}

              {/* Organic vein/root system */}
              {stage.energyFlow > 0 && (
                <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 110" preserveAspectRatio="xMidYMid meet" style={{ opacity: stage.energyFlow * 0.7 + 0.3 }}>
                  {ALL_VEIN_PATHS.map((d, i) => {
                    const w = getPathWidth(i) * 0.85;
                    const colorOpacity = 0.3 + stage.energyFlow * 0.4;
                    return (
                      <motion.path
                        key={`vein-${i}`} d={d}
                        stroke={getPathColor(i, colorOpacity)}
                        strokeWidth={w} fill="none" strokeLinecap="round"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 2.5, delay: i * 0.08, ease: [0.22, 0.61, 0.36, 1] }}
                        style={{ filter: `drop-shadow(0 0 ${2 + stage.energyFlow * 4}px hsla(42, 85%, 55%, 0.5))` }}
                      />
                    );
                  })}
                </svg>
              )}

              {/* Fog */}
              <div className="absolute inset-0 pointer-events-none" style={{
                background: `radial-gradient(ellipse at 50% 80%, rgba(11, 27, 22, ${stage.skyPhase >= 3 ? 0.2 : 0.6}) 0%, transparent 70%)`,
              }} />

              {/* 50+ aura */}
              {fruits.length >= 50 && (
                <motion.div className="absolute inset-0 pointer-events-none" style={{
                  background: 'radial-gradient(ellipse at 50% 50%, hsla(40, 80%, 55%, 0.1) 0%, transparent 50%)',
                }} animate={{ opacity: [0.3, 0.7, 0.3], scale: [0.98, 1.02, 0.98] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                />
              )}

              {/* 100+ crown glow */}
              {fruits.length >= 100 && (
                <motion.div className="absolute pointer-events-none" style={{
                  top: '5%', left: '25%', right: '25%', height: '30%',
                  background: 'radial-gradient(ellipse at 50% 50%, hsla(45, 90%, 65%, 0.15) 0%, transparent 70%)',
                  filter: 'blur(10px)',
                }} animate={{ opacity: [0.4, 0.8, 0.4], scale: [0.95, 1.05, 0.95] }}
                  transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                />
              )}

              {/* Glowing leaves */}
              {fruits.length >= 10 && Array.from({ length: 5 }).map((_, i) => (
                <motion.div key={`glow-leaf-${i}`} className="absolute pointer-events-none rounded-full"
                  style={{ width: 6, height: 6, left: `${30 + i * 10}%`, top: `${20 + (i * 7) % 30}%`,
                    background: `radial-gradient(circle, hsla(${stage.skyPhase >= 3 ? '45, 80%, 60%' : '120, 50%, 50%'}, 0.6), transparent)`, filter: 'blur(2px)',
                  }}
                  animate={{ opacity: [0.2, 0.6, 0.2], scale: [0.8, 1.3, 0.8] }}
                  transition={{ duration: 3 + i * 0.5, delay: i * 0.4, repeat: Infinity, ease: 'easeInOut' }}
                />
              ))}

              {/* Birds */}
              {fruits.length >= 5 && [
                { x: '25%', y: '18%', delay: 0 },
                { x: '72%', y: '22%', delay: 1.5 },
              ].map((bird, i) => (
                <motion.div key={`bird-${i}`} className="absolute pointer-events-none" style={{ left: bird.x, top: bird.y }}
                  animate={{ y: [0, -3, 0, -2, 0], x: [0, 2, -1, 3, 0] }}
                  transition={{ duration: 6, delay: bird.delay, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <svg width="12" height="8" viewBox="0 0 12 8" style={{ opacity: 0.5 }}>
                    <path d="M0 4 Q3 0 6 4 Q9 0 12 4" stroke="rgba(200,220,200,0.7)" strokeWidth="1" fill="none" />
                  </svg>
                </motion.div>
              ))}

              <FireflyParticles count={isEmpty ? 10 : 30} containerRef={containerRef} />

              {/* Read-only fruit nodes */}
              {positioned.map(({ x, y }, i) => (
                <PublicFruitNode key={i} x={x} y={y} index={i} />
              ))}

              {isEmpty && <EmptyTreeState />}

              {/* Ground glow */}
              <div className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none" style={{
                background: stage.skyPhase >= 2
                  ? 'linear-gradient(to top, hsla(35, 50%, 30%, 0.1), transparent)'
                  : 'linear-gradient(to top, hsla(120, 40%, 40%, 0.06), transparent)',
              }} />
            </div>
          </motion.div>
        </div>

        {/* ─── Privacy notice ─── */}
        <motion.div
          className="mt-5 text-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted/30 border border-border/20 text-muted-foreground text-xs">
            <Lock className="h-3 w-3" />
            Messages are private — only the tree's growth is visible
          </div>
        </motion.div>

        {/* ─── Emotional Context ─── */}
        <motion.div
          className="mt-8 text-center px-6 max-w-md mx-auto"
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.7 }}
        >
          <p className="text-sm md:text-base text-muted-foreground/70 italic leading-relaxed">
            "This didn't grow overnight… it grew from moments that mattered."
          </p>
        </motion.div>

        {/* ─── Primary CTA ─── */}
        <motion.section
          className="mt-10 text-center px-4 max-w-md mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.7 }}
        >
          <Link to="/signup">
            <Button size="lg" className="rounded-2xl gap-2 w-full max-w-xs shadow-xl text-base">
              <TreePine className="h-5 w-5" />
              Start Your Own Living Tree
            </Button>
          </Link>
          <p className="text-xs text-muted-foreground/60 mt-3 leading-relaxed">
            Start sharing what matters while it still can be heard.
          </p>
        </motion.section>

        {/* ─── Secondary CTA ─── */}
        <motion.section
          className="mt-8 text-center px-4 max-w-md mx-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
        >
          <Link to="/try-create">
            <Button variant="outline" size="default" className="rounded-xl gap-2 border-primary/30 hover:bg-primary/5">
              <Send className="h-4 w-4" />
              Send a message to someone who matters
            </Button>
          </Link>
        </motion.section>

        {/* ─── Share / Viral Loop ─── */}
        <motion.section
          className="mt-10 mb-12 text-center px-4 max-w-md mx-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
        >
          <p className="text-sm text-muted-foreground/50 mb-3 flex items-center justify-center gap-1.5">
            <Heart className="h-3.5 w-3.5" />
            Who would you grow this with?
          </p>
          <div className="flex items-center justify-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="rounded-xl gap-1.5 text-muted-foreground hover:text-foreground"
              onClick={handleNativeShare}
            >
              <Share2 className="h-4 w-4" />
              Share
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="rounded-xl gap-1.5 text-muted-foreground hover:text-foreground"
              onClick={handleShareLink}
            >
              <Link2 className="h-4 w-4" />
              Copy Link
            </Button>
          </div>
        </motion.section>

        {/* ─── Footer attribution ─── */}
        <footer className="text-center pb-8 px-4">
          <p className="text-[11px] text-muted-foreground">
            Written with{' '}
            <Link to="/" className="underline decoration-muted-foreground/40 hover:text-foreground transition-colors">
              Say It NOW
            </Link>
            {' '}— A private space for saying what matters.
          </p>
        </footer>
      </main>
    </div>
  );
}
